/**
 * Formateo de métricas compartido (tarjetas, ejes de gráficos, tooltips).
 *
 * Los formatos provienen del backend del asistente: `currency`, `gallons`,
 * `percent` y `number`.
 */

export type MetricFormat = 'currency' | 'gallons' | 'percent' | 'number' | string;

const NUMBER = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 2 });
const NUMBER_INT = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 0 });
const CURRENCY = new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'PEN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const COMPACT = new Intl.NumberFormat('es-PE', { notation: 'compact', maximumFractionDigits: 1 });

/** Valor listo para mostrar al usuario. */
export function formatMetricValue(value: unknown, format: MetricFormat = 'number'): string {
  const number = Number(value);

  if (!Number.isFinite(number)) return '—';

  switch (format) {
    case 'currency':
      return CURRENCY.format(number);
    case 'gallons':
      return `${NUMBER.format(number)} gal`;
    case 'percent':
      return `${NUMBER.format(number)} %`;
    default:
      return NUMBER.format(number);
  }
}

/** Etiqueta compacta para los ejes de un gráfico. */
export function formatAxisValue(value: number, format: MetricFormat = 'number'): string {
  if (!Number.isFinite(value)) return '';

  switch (format) {
    case 'currency':
      // `Intl` genera el sufijo compacto en minúscula (`350 k`).
      return `S/ ${COMPACT.format(value).replace(/\s?k$/i, ' K')}`;
    case 'gallons':
      return `${COMPACT.format(value)}`;
    case 'percent':
      return `${NUMBER_INT.format(value)}%`;
    default:
      return COMPACT.format(value);
  }
}

/**
 * Valor abreviado cuando hay poco espacio (centro de una dona, tarjetas).
 * En montos de millones se evita que el número se salga del gráfico; el detalle
 * exacto sigue visible en la leyenda.
 */
export function formatMetricValueCompact(value: unknown, format: MetricFormat = 'number'): string {
  const numero = Number(value);

  if (!Number.isFinite(numero)) return '—';

  if (format === 'currency' && Math.abs(numero) >= 1_000_000) {
    return `S/ ${COMPACT.format(numero).replace(/\s?k$/i, ' K')}`;
  }

  return formatMetricValue(numero, format);
}

export const formatNumber = (value: unknown): string => {
  const number = Number(value);
  return Number.isFinite(number) ? NUMBER.format(number) : '—';
};
